/**
 * The layout of an image post follows from how many photos it carries.
 *
 * The API stores the album (`photoAlbum`) but not the layout it was composed
 * with, so the composer (before upload) and the feed mappers (after read) have
 * to agree on the same inference or a post comes back wearing a layout it was
 * never created with.
 */
import { POST_IMAGE_LAYOUT } from '../constants/createPost';
import type { PostImageLayout } from '../types/post';

/**
 * @param count photos in the album.
 * @param current layout already chosen, when there is one. Reads pass `null`
 *   because the API answers with the album alone.
 */
export const resolveLayoutForAlbum = (
  count: number,
  current: PostImageLayout | null,
): PostImageLayout | null => {
  // Gallery is an explicit user choice: respect it regardless of count.
  // Comparison layouts are count-driven (2 -> Comparison2, 4 -> Comparison4).
  if (current === POST_IMAGE_LAYOUT.GALLERY) return POST_IMAGE_LAYOUT.GALLERY;
  if (count === 4) return POST_IMAGE_LAYOUT.COMPARISON_4;
  if (count === 2) return POST_IMAGE_LAYOUT.COMPARISON_2;
  if (count === 1) return POST_IMAGE_LAYOUT.SINGLE;
  if (count >= 3) return POST_IMAGE_LAYOUT.GALLERY;
  // No album: video, GIF or text-only posts keep whatever the composer holds.
  return count === 0 ? current : null;
};
